// src/contexts/AuthContext.jsx
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { authApi, AUTH_TOKEN_KEY } from '../api/authApi';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem(AUTH_TOKEN_KEY));
  const [isLoading, setIsLoading] = useState(true);

  const clearSession = useCallback(() => {
    localStorage.removeItem(AUTH_TOKEN_KEY);
    setToken(null);
    setUser(null);
  }, []);

  useEffect(() => {
    const loadSession = async () => {
      if (!token) {
        setIsLoading(false);
        return;
      }

      try {
        const data = await authApi.me();
        setUser(data.user);
      } catch (err) {
        clearSession();
      } finally {
        setIsLoading(false);
      }
    };

    loadSession();
  }, [token, clearSession]);

  const saveSession = useCallback((data) => {
    localStorage.setItem(AUTH_TOKEN_KEY, data.token);
    setToken(data.token);
    setUser(data.user);
  }, []);

  const login = useCallback(async ({ login: loginValue, password }) => {
    const data = await authApi.login({ login: loginValue, password });
    saveSession(data);
    return data;
  }, [saveSession]);

  const register = useCallback(async ({ username, email, password }) => {
    const data = await authApi.register({ username, email, password });
    saveSession(data);
    return data;
  }, [saveSession]);

  const logout = useCallback(async () => {
    try {
      if (token) {
        await authApi.logout();
      }
    } finally {
      clearSession();
    }
  }, [token, clearSession]);

  const value = useMemo(() => ({
    user,
    token,
    isLoading,
    isAuthenticated: !!user && !!token,
    login,
    register,
    logout,
    clearSession,
  }), [user, token, isLoading, login, register, logout, clearSession]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
};

export default AuthContext;
